import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface LibraryFilters {
  search: string;
  genres: string[];
  moods: string[];
  statuses: string[];
  /** Multi-select tag filter (FR-060) — AND / intersection semantics applied server-side. */
  tags: string[];
  sortBy: string;
  sortOrder: string;
}

export interface UseLibraryFiltersReturn {
  /** Current filter values (search is debounced) */
  filters: LibraryFilters;
  /** Raw search input value (not debounced) */
  searchInput: string;
  setSearchInput: (value: string) => void;
  /** Toggle a genre in/out of the selected list */
  toggleGenre: (value: string) => void;
  /** Replace the entire genres list (for single-select dropdowns) */
  setGenres: (values: string[]) => void;
  /** Toggle a mood in/out of the selected list */
  toggleMood: (value: string) => void;
  /** Replace the entire moods list */
  setMoods: (values: string[]) => void;
  /** Toggle a status in/out of the selected list */
  toggleStatus: (value: string) => void;
  /** Replace the entire statuses list */
  setStatuses: (values: string[]) => void;
  /** Replace the entire tags list (multi-select combobox in filter UI) */
  setTags: (values: string[]) => void;
  setSortBy: (value: string) => void;
  setSortOrder: (value: string) => void;
  resetFilters: () => void;
  /** True if any filter is active */
  hasActiveFilters: boolean;
}

const DEBOUNCE_MS = 300;

function parseCommaSeparated(param: string | null): string[] {
  if (!param) return [];
  return param.split(',').map(s => s.trim()).filter(Boolean);
}

function toggleInArray(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
}

/**
 * Manages library browser filter state synced to URL search params.
 * Search input is debounced by 300ms before propagating to filters.
 * Genre, mood, and status support multi-select (comma-separated in URL).
 */
export function useLibraryFilters(): UseLibraryFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInputState] = useState(searchParams.get('search') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchInput);
  const [genres, setGenresState] = useState<string[]>(parseCommaSeparated(searchParams.get('genres')));
  const [moods, setMoodsState] = useState<string[]>(parseCommaSeparated(searchParams.get('moods')));
  const [statuses, setStatusesState] = useState<string[]>(parseCommaSeparated(searchParams.get('statuses')));
  const [tags, setTagsState] = useState<string[]>(parseCommaSeparated(searchParams.get('tags')));
  const [sortBy, setSortByState] = useState(searchParams.get('sortBy') ?? '');
  const [sortOrder, setSortOrderState] = useState(searchParams.get('sortOrder') ?? '');

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce search input
  useEffect(() => {
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchInput]);

  // Sync filters to URL search params
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (genres.length > 0) params.set('genres', genres.join(','));
    if (moods.length > 0) params.set('moods', moods.join(','));
    if (statuses.length > 0) params.set('statuses', statuses.join(','));
    if (tags.length > 0) params.set('tags', tags.join(','));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, genres, moods, statuses, tags, sortBy, sortOrder, setSearchParams]);

  const setSearchInput = useCallback((value: string) => {
    setSearchInputState(value);
  }, []);

  const toggleGenre = useCallback((value: string) => {
    setGenresState(prev => toggleInArray(prev, value));
  }, []);

  const setGenres = useCallback((values: string[]) => {
    setGenresState(values);
  }, []);

  const toggleMood = useCallback((value: string) => {
    setMoodsState(prev => toggleInArray(prev, value));
  }, []);

  const setMoods = useCallback((values: string[]) => {
    setMoodsState(values);
  }, []);

  const toggleStatus = useCallback((value: string) => {
    setStatusesState(prev => toggleInArray(prev, value));
  }, []);

  const setStatuses = useCallback((values: string[]) => {
    setStatusesState(values);
  }, []);

  const setTags = useCallback((values: string[]) => {
    setTagsState(values);
  }, []);

  const setSortBy = useCallback((value: string) => {
    setSortByState(value);
  }, []);

  const setSortOrder = useCallback((value: string) => {
    setSortOrderState(value);
  }, []);

  const resetFilters = useCallback(() => {
    setSearchInputState('');
    setDebouncedSearch('');
    setGenresState([]);
    setMoodsState([]);
    setStatusesState([]);
    setTagsState([]);
    setSortByState('');
    setSortOrderState('');
  }, []);

  const filters: LibraryFilters = {
    search: debouncedSearch,
    genres,
    moods,
    statuses,
    tags,
    sortBy,
    sortOrder,
  };

  const hasActiveFilters = Boolean(
    debouncedSearch ||
    genres.length > 0 ||
    moods.length > 0 ||
    statuses.length > 0 ||
    tags.length > 0,
  );

  return {
    filters,
    searchInput,
    setSearchInput,
    toggleGenre,
    setGenres,
    toggleMood,
    setMoods,
    toggleStatus,
    setStatuses,
    setTags,
    setSortBy,
    setSortOrder,
    resetFilters,
    hasActiveFilters,
  };
}
